import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import { useAuth } from '../context/AuthContext';
import { useProjectAuth } from '../utils/authUtils';
import { fetchEditingDetails, updateRequestStatus, updateRevisionStatus } from '../graphqlService';
import { getApiBaseUrl } from '../apolloClient';
import { brand, neutral } from '../theme';
import { formatDate } from '../utils/format';
import { isFinishedStatus, resolveRevisionLimit } from '../utils/projectStatus';
import { normalizeFps } from '../utils/timecode';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import StatusChip from './ui/StatusChip';
import LoadingState from './ui/LoadingState';
import EmptyState from './ui/EmptyState';
import ProjectNotes from './ProjectNotes';
import RevisionsTab from './RevisionsTab';
import ReviewPlayer from './ProjectWorkspace/ReviewPlayer';
import RevisionDecisionDialog from './ProjectWorkspace/RevisionDecisionDialog';
import ReviewNotesPanel from './ProjectWorkspace/ReviewNotesPanel';
import TaskListPanel from './ProjectWorkspace/TaskListPanel';
import BriefPanel from './ProjectWorkspace/BriefPanel';
import ActivityPanel from './ProjectWorkspace/ActivityPanel';
import { useRevisionFiles } from './ProjectWorkspace/useRevisionFiles';
import { saveFile } from './ProjectWorkspace/revisionMedia';
import {
  DecisionType,
  ORIGINAL_KEY,
  approvedCutOf,
  creatorGuidance,
  describeAttachments,
  describeChangeRounds,
  isAbortError,
  isMulticamSource,
  isReadyRevision,
  readableError,
} from './ProjectWorkspace/reviewModel';
import type { ProjectData } from './ProjectWorkspace/types';
import { RevisionLike, buildRevisionVersions } from './EditorWorkspace/workflow';
import { summarizeTasks } from './EditorWorkspace/taskMeta';

const TAB_KEYS = ['notes', 'brief', 'tasks', 'revisions', 'activity'] as const;
type TabKey = typeof TAB_KEYS[number];

type NoteType = 'GENERAL' | 'REVISION_REQUEST' | 'CLARIFICATION' | 'SYSTEM';

/** The sticky side panel sits below the app bar (64px) with a little air. */
const STICKY_TOP = 88;

const tabLabel = (label: string, count?: React.ReactNode) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    {count !== undefined && count !== null && (
      <Box
        component="span"
        sx={{
          px: 0.75,
          minWidth: 20,
          height: 20,
          borderRadius: '10px',
          bgcolor: neutral[200],
          color: 'text.secondary',
          fontSize: 11.5,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

/** Bound to the fact before it, so a line never starts with a dot. */
const MetaSeparator = () => (
  <Box component="span" aria-hidden="true" sx={{ color: brand.borderStrong }}>
    {' · '}
  </Box>
);

/** Only High and Urgent are called out; the rest is the default. */
const priorityCallout = (priority?: string | null) => {
  const key = String(priority || '').toUpperCase();
  if (key === 'URGENT') return 'Urgent';
  if (key === 'HIGH') return 'High priority';
  return null;
};

/**
 * The creator's project page, review first: the latest cut in a large
 * player with every version and the original footage a click away, one
 * Approve / Request changes decision per cut, and the brief, tasks,
 * revisions and activity below. Messages stay in view in a side panel.
 * Every notification about a project links here.
 */
const ProjectDetails: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  // A fresh page per project: a notification can link from one project to
  // another while this page is open, and nothing should carry over.
  return <ProjectReviewPage key={projectId || 'none'} projectId={projectId || ''} />;
};

const ProjectReviewPage: React.FC<{ projectId: string }> = ({ projectId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getToken } = useAuth();

  const [project, setProject] = useState<ProjectData | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error' | 'missing'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('notes');
  /** The version the viewer picked; null until they pick one. */
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const [playRequest, setPlayRequest] = useState<{ key: string; nonce: number } | null>(null);
  const [seekRequest, setSeekRequest] = useState<{ time: number; nonce: number } | null>(null);
  const [decision, setDecision] = useState<{ type: DecisionType; revisionId: string } | null>(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [closingProject, setClosingProject] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [packageBusy, setPackageBusy] = useState(false);

  const playerRef = useRef<HTMLDivElement | null>(null);
  const requestSeq = useRef(0);
  const nonce = useRef(0);
  const hasProject = useRef(false);
  const files = useRevisionFiles(getToken);
  const { load: loadRevisionFile, pin: pinRevisionFile } = files;
  const { userRole } = useProjectAuth(user, project);

  /**
   * Loads the project. After the first load it refreshes in place, so
   * sending a message or deciding on a cut doesn't blank the page.
   */
  const loadProject = useCallback(async () => {
    if (!projectId || !user) return;
    const seq = ++requestSeq.current;
    const silent = hasProject.current;
    if (!silent) setLoadState('loading');
    try {
      const token = await getToken();
      if (!token) throw new Error('Your session has expired. Sign in again to see this project.');
      const data = await fetchEditingDetails(projectId, token);
      if (seq !== requestSeq.current) return;
      if (!data) {
        if (silent) return;
        setProject(null);
        setLoadState('missing');
        return;
      }
      hasProject.current = true;
      setProject(data);
      setLoadState('ready');
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('Error loading project details:', error);
      if (silent) {
        setPageError('We couldn’t refresh this project. Reload the page to see the latest changes.');
      } else {
        setLoadError(readableError(error, 'We couldn’t load this project. Check your connection and try again.'));
        setLoadState('error');
      }
    }
  }, [projectId, user, getToken]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  const revisions = useMemo(() => (project?.revisions || []).filter(Boolean) as RevisionLike[], [project]);
  const projectNotes = useMemo(() => (project?.notes || []).filter(Boolean), [project]);
  const versions = useMemo(() => buildRevisionVersions(revisions, projectNotes), [revisions, projectNotes]);
  const annotations = useMemo(() => (project?.annotations || []).filter(Boolean), [project]);
  const tasks = useMemo(() => (project?.tasks || []).filter(Boolean), [project]);
  const notesForPanel = useMemo(
    () => projectNotes.map((note) => ({ ...note, type: note.type as NoteType })),
    [projectNotes]
  );

  const isOwner = Boolean(user?.uid && project?.userId === user.uid);
  const isCreator = userRole === 'creator';
  const statusKey = String(project?.status || '').toUpperCase();
  const finished = isFinishedStatus(project?.status);
  // The API takes a decision on a cut only while the project is in review
  // (CLIENT_REVIEW -> FINAL_APPROVAL or REVISION_REQUESTED). A READY cut left
  // over from an older flow must not offer a decision that can only fail, or
  // hide "Close project" on a project that's already approved.
  const canDecide = isCreator && !finished && statusKey === 'CLIENT_REVIEW';
  const waiting = canDecide ? versions.find((entry) => isReadyRevision(entry.revision.status)) : undefined;
  const multicam =
    isMulticamSource(project?.sourceType) && (project?.multicamSnapshot?.angles?.length ?? 0) > 0
      ? project?.multicamSnapshot ?? null
      : null;
  const hasOriginal = Boolean(project?.videoUrl || multicam);
  // Once approved, the approved cut leads: an editor can still upload after
  // approval, and that upload hasn't been reviewed.
  const { approved, unreviewedAfter } = approvedCutOf(versions, project?.status);
  // Start on the cut that's waiting for a decision (else the approved cut,
  // else the latest), and keep the viewer's own choice across refreshes
  // while it still exists.
  const defaultKey =
    waiting?.revision.id ?? approved?.revision.id ?? versions[0]?.revision.id ?? (hasOriginal ? ORIGINAL_KEY : null);
  const chosenIsValid =
    chosenKey === ORIGINAL_KEY ? hasOriginal : Boolean(chosenKey && versions.some((entry) => entry.revision.id === chosenKey));
  const selectedKey = chosenKey && chosenIsValid ? chosenKey : defaultKey;
  const editorName = project?.editorName || project?.editor?.name || null;
  const revisionLimit = resolveRevisionLimit(project?.revisionLimit);
  const revisionCount = typeof project?.revisionCount === 'number' ? project.revisionCount : 0;
  const fps = normalizeFps(project?.sourceFrameRate);
  const taskSummary = summarizeTasks(tasks);
  const images = (project?.images || []).filter(Boolean).length;
  const sounds = (project?.sounds || []).filter(Boolean).length;

  useEffect(() => {
    pinRevisionFile(selectedKey && selectedKey !== ORIGINAL_KEY ? selectedKey : null);
  }, [selectedKey, pinRevisionFile]);

  // Note emails link to /project-details/<id>#notes. The panel only exists
  // once the project has rendered, so the browser's own anchor jump misses
  // it; scroll once, after the first load.
  const scrolledToNotes = useRef(false);
  useEffect(() => {
    if (location.hash !== '#notes' || !project || scrolledToNotes.current) return;
    const panel = document.getElementById('notes');
    if (!panel) return;
    scrolledToNotes.current = true;
    panel.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [location.hash, project]);

  const scrollToPlayer = () => playerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const handlePlayVersion = (revisionId: string) => {
    nonce.current += 1;
    setChosenKey(revisionId);
    setPlayRequest({ key: revisionId, nonce: nonce.current });
    scrollToPlayer();
  };

  const handleShowInFootage = (time: number) => {
    nonce.current += 1;
    setChosenKey(ORIGINAL_KEY);
    setSeekRequest({ time, nonce: nonce.current });
    scrollToPlayer();
  };

  const handleDownloadRevision = async (revision: RevisionLike, version: number) => {
    if (downloadingId) return;
    setDownloadingId(revision.id);
    setPageError(null);
    try {
      const url = await loadRevisionFile(revision);
      saveFile(url, revision.filename || `v${version}.mp4`);
    } catch (error) {
      if (!isAbortError(error)) {
        setPageError(readableError(error, 'We couldn’t download this version. Please try again.'));
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const openDecision = (type: DecisionType, revisionId: string) => {
    setDecision({ type, revisionId });
    setDecisionOpen(true);
  };

  const decisionVersion = decision ? versions.find((entry) => entry.revision.id === decision.revisionId) : undefined;

  /** Same status change as before: the revision is accepted or sent back. */
  const handleConfirmDecision = async (comments: string) => {
    if (!decision) return;
    const token = await getToken();
    if (!token) throw new Error('Your session has expired. Sign in again to continue.');
    await updateRevisionStatus(decision.revisionId, decision.type === 'approve' ? 'ACCEPTED' : 'REVISION_REQUESTED', comments, token);
    const label = decisionVersion ? `v${decisionVersion.version}` : 'The cut';
    setDecisionOpen(false);
    setToast(decision.type === 'approve' ? `${label} approved` : `Changes requested on ${label}`);
    await loadProject();
  };

  /** FINAL_APPROVAL -> DELIVERED: the creator marking the job finished. */
  const handleCloseProject = async () => {
    if (!projectId || closingProject) return;
    setClosingProject(true);
    setCloseError(null);
    try {
      const token = await getToken();
      if (!token) {
        setCloseError('Your session has expired. Sign in again to close the project.');
        return;
      }
      await updateRequestStatus(projectId, 'DELIVERED', 'Project closed by creator', null, null, token);
      setConfirmCloseOpen(false);
      setToast('Project closed');
      await loadProject();
    } catch (error) {
      console.error('Failed to close project:', error);
      // Shown in the dialog, which stays open so the creator can retry.
      setCloseError(readableError(error, 'Could not close the project. Please try again.'));
    } finally {
      setClosingProject(false);
    }
  };

  /** The brief, notes and asset links as one ZIP (the handoff package). */
  const handleDownloadPackage = async () => {
    if (!projectId || packageBusy) return;
    setPackageBusy(true);
    setPageError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Your session has expired. Sign in again to download.');
      const response = await fetch(`${getApiBaseUrl()}/api/project/${projectId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('We couldn’t prepare the download. Please try again.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const base = (project?.projectName || 'project').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'project';
      saveFile(url, `${base}_assets.zip`);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading project assets:', error);
      setPageError(readableError(error, 'We couldn’t prepare the download. Please try again.'));
    } finally {
      setPackageBusy(false);
    }
  };

  if (!project) {
    return (
      <PageContainer width="wide">
        {loadState === 'loading' ? (
          <>
            <PageHeader
              backTo={{ to: '/my-projects', label: 'Projects' }}
              title={<Skeleton width="min(420px, 70vw)" />}
              meta={<Skeleton width={280} />}
            />
            <LoadingState variant="detail" label="Loading project" />
          </>
        ) : loadState === 'error' ? (
          <>
            <PageHeader backTo={{ to: '/my-projects', label: 'Projects' }} title="Project" />
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void loadProject()}>
                  Retry
                </Button>
              }
            >
              {loadError || 'We couldn’t load this project. Check your connection and try again.'}
            </Alert>
          </>
        ) : (
          <>
            <PageHeader backTo={{ to: '/my-projects', label: 'Projects' }} title="Project not found" />
            <Section>
              <EmptyState
                compact
                icon={<FolderOffOutlinedIcon />}
                title="This project isn’t available"
                description="It may have been deleted, or you may not have access to it."
                actionLabel="Back to projects"
                onAction={() => navigate('/my-projects')}
              />
            </Section>
          </>
        )}
      </PageContainer>
    );
  }

  const guidance = isCreator
    ? creatorGuidance({
        status: project.status,
        waitingVersion: waiting?.version,
        versionCount: versions.length,
        approvedVersion: approved?.version,
        unreviewedAfter,
        editorName,
        isOwner,
      })
    : undefined;

  const callout = priorityCallout(project.priority);
  const meta = (
    <>
      <Box component="span" data-testid="project-status" sx={{ display: 'inline-flex' }}>
        <StatusChip kind="project" status={project.status} />
      </Box>
      {/* One run of text so it wraps like a sentence. */}
      <Box component="span" sx={{ minWidth: 0 }}>
        <span>{editorName ? `Editor: ${editorName}` : 'No editor yet'}</span>
        {project.deadline && (
          <>
            <MetaSeparator />
            <span>Due {formatDate(project.deadline)}</span>
          </>
        )}
        <MetaSeparator />
        <Box component="span" data-testid="change-rounds" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {describeChangeRounds(revisionCount, revisionLimit)}
        </Box>
        {callout && (
          <>
            <MetaSeparator />
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'bottom', color: 'text.primary', fontWeight: 600 }}
            >
              <FlagOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              {callout}
            </Box>
          </>
        )}
      </Box>
    </>
  );

  const headerAction = waiting ? (
    <>
      <Button variant="outlined" onClick={() => openDecision('changes', waiting.revision.id)}>
        Request changes
      </Button>
      <Button variant="contained" startIcon={<CheckOutlinedIcon />} onClick={() => openDecision('approve', waiting.revision.id)}>
        Approve v{waiting.version}
      </Button>
    </>
  ) : statusKey === 'FINAL_APPROVAL' && isOwner ? (
    <Button
      variant="contained"
      startIcon={<CheckOutlinedIcon />}
      onClick={() => {
        setCloseError(null);
        setConfirmCloseOpen(true);
      }}
      disabled={closingProject}
    >
      Close project
    </Button>
  ) : userRole === 'editor' ? (
    <Button variant="outlined" component={RouterLink} to={`/edit-projects/${project.id}`}>
      Open editor workspace
    </Button>
  ) : undefined;

  const attachments = describeAttachments(annotations.length, images, sounds);
  const panels: Record<TabKey, React.ReactNode> = {
    notes: (
      <Section
        disablePadding
        description={
          project.videoUrl && !multicam
            ? 'Your notes on the original footage. Select a timecode to see that moment.'
            : 'Your notes on the original footage.'
        }
      >
        <ReviewNotesPanel
          notes={annotations}
          tasks={tasks}
          fps={fps}
          onShowInFootage={project.videoUrl && !multicam ? handleShowInFootage : undefined}
        />
      </Section>
    ),
    brief: (
      <Section
        description={attachments || 'No notes or files attached.'}
        action={
          <Button
            size="small"
            startIcon={packageBusy ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
            onClick={() => void handleDownloadPackage()}
            disabled={packageBusy}
          >
            Download brief and assets
          </Button>
        }
      >
        <BriefPanel project={project} editorName={editorName} />
      </Section>
    ),
    tasks: (
      <Section
        disablePadding
        description={
          tasks.length > 0
            ? `${taskSummary.completed} of ${taskSummary.total} done${taskSummary.inProgress ? ` · ${taskSummary.inProgress} in progress` : ''}`
            : undefined
        }
      >
        <TaskListPanel tasks={tasks} fps={fps} />
      </Section>
    ),
    revisions: (
      <Section
        disablePadding
        description={versions.length > 0 ? `Newest first · ${describeChangeRounds(revisionCount, revisionLimit)}` : undefined}
      >
        <RevisionsTab
          revisions={revisions}
          notes={projectNotes}
          userRole={userRole}
          canDecide={canDecide}
          activeId={selectedKey}
          unreviewedAfter={unreviewedAfter}
          downloadingId={downloadingId}
          onPlay={handlePlayVersion}
          onDownload={(revision, version) => void handleDownloadRevision(revision, version)}
          onDecide={openDecision}
        />
      </Section>
    ),
    activity: (
      <Section disablePadding>
        <ActivityPanel history={project.statusHistory || []} />
      </Section>
    ),
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        backTo={{ to: '/my-projects', label: 'Projects' }}
        title={project.projectName || 'Untitled project'}
        subtitle={guidance}
        meta={meta}
        action={headerAction}
      />

      {pageError && (
        <Alert severity="error" onClose={() => setPageError(null)} sx={{ mb: 3 }}>
          {pageError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px', xl: 'minmax(0, 1fr) 400px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box ref={playerRef} sx={{ scrollMarginTop: STICKY_TOP }}>
            <ReviewPlayer
              versions={versions}
              selectedKey={selectedKey}
              onSelect={setChosenKey}
              files={files}
              playRequest={playRequest}
              seekRequest={seekRequest}
              original={{ videoUrl: project.videoUrl, videoName: project.videoName, multicam }}
              notes={annotations}
              fps={fps}
              onDownload={(revision, version) => void handleDownloadRevision(revision, version)}
              downloadingId={downloadingId}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Tabs
              value={tab}
              onChange={(_, value: TabKey) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Project sections"
              sx={{ borderBottom: `1px solid ${brand.border}`, mb: 2 }}
            >
              <Tab value="notes" id="project-tab-notes" aria-controls="project-panel-notes" label={tabLabel('Review notes', annotations.length || undefined)} />
              <Tab value="brief" id="project-tab-brief" aria-controls="project-panel-brief" label={tabLabel('Brief')} />
              <Tab
                value="tasks"
                id="project-tab-tasks"
                aria-controls="project-panel-tasks"
                label={tabLabel('Tasks', tasks.length ? `${taskSummary.completed}/${taskSummary.total}` : undefined)}
              />
              <Tab
                value="revisions"
                id="project-tab-revisions"
                aria-controls="project-panel-revisions"
                label={tabLabel('Revisions', versions.length || undefined)}
              />
              <Tab value="activity" id="project-tab-activity" aria-controls="project-panel-activity" label={tabLabel('Activity')} />
            </Tabs>
            <Box role="tabpanel" id={`project-panel-${tab}`} aria-labelledby={`project-tab-${tab}`}>
              {panels[tab]}
            </Box>
          </Box>
        </Box>

        {/* id="notes": target of the #notes links in note emails. Sticky on
            wide screens so the conversation and its composer stay in view. */}
        <Box
          id="notes"
          sx={{
            minWidth: 0,
            scrollMarginTop: STICKY_TOP,
            position: { lg: 'sticky' },
            top: { lg: STICKY_TOP },
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { lg: `calc(100vh - ${STICKY_TOP + 24}px)` },
          }}
        >
          <ProjectNotes
            projectId={project.id}
            notes={notesForPanel}
            onNoteAdded={() => void loadProject()}
            sx={{ height: 'auto', flex: '0 1 auto' }}
          />
        </Box>
      </Box>

      <RevisionDecisionDialog
        open={decisionOpen && Boolean(decisionVersion)}
        type={decision?.type ?? 'approve'}
        version={decisionVersion?.version ?? 1}
        nextVersion={versions.length + 1}
        roundsUsed={revisionCount}
        roundsLimit={revisionLimit}
        editorName={editorName}
        onClose={() => setDecisionOpen(false)}
        onConfirm={handleConfirmDecision}
      />

      <Dialog
        open={confirmCloseOpen}
        onClose={() => {
          if (!closingProject) setConfirmCloseOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Close this project?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>It will be marked as delivered and your editor will be notified.</DialogContentText>
          <DialogContentText>You won’t be able to request more changes after this.</DialogContentText>
          {closeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {closeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmCloseOpen(false)} disabled={closingProject}>
            Not yet
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCloseProject()}
            disabled={closingProject}
            startIcon={closingProject ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {closingProject ? 'Closing…' : 'Close project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageContainer>
  );
};

export default ProjectDetails;
